/**
 * Geo-Location Market Suggestion Popup (BIOEFFECT-style)
 *
 * Modules:
 *  - GeoDetection      : detect visitor country via Shopify browsing-context API
 *  - MarketMapping     : country ISO -> { locale, countryCode, currency }
 *  - Translations      : language strings for popup UI (en/is/fr/it)
 *  - Storage           : sessionStorage gate (popup reappears each new browser session)
 *  - BucksFallback     : currency switch via BUCKS Currency Converter, post-redirect
 *  - GeoLocalePopup    : web component (intro view + country picker view)
 */
(() => {
  if (customElements.get('geo-locale-popup')) return;

  const COUNTRY_LOCALE_MAP = {
    IS: 'is',
    IT: 'it',
    FR: 'fr',
  };

  const DEFAULT_LOCALE = 'en';
  const DEFAULT_COUNTRY = 'US';

  const BUCKS_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'ISK'];

  const CURRENCY_TEXT_ALIASES = {
    CAD: ['CAD', 'CA$', 'C$', 'CANADIAN'],
  };

  const CURRENCY_SYMBOLS = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: '$',
    ISK: 'kr',
  };

  // Country ISO → native currency (only currencies tracked by BUCKSCC).
  // Anything not listed here resolves to USD.
  const COUNTRY_BUCKS_CURRENCY = {
    US: 'USD',
    GB: 'GBP',
    CA: 'CAD',
    IS: 'ISK',
    // Eurozone
    AT: 'EUR', BE: 'EUR', BG: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR',
    FR: 'EUR', DE: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR',
    LV: 'EUR', LT: 'EUR', LU: 'EUR', MT: 'EUR', NL: 'EUR',
    PT: 'EUR', SK: 'EUR', SI: 'EUR', ES: 'EUR', HR: 'EUR',
    MC: 'EUR', AD: 'EUR', SM: 'EUR', VA: 'EUR', ME: 'EUR',
    XK: 'EUR',
  };

  function resolveBucksCurrency(countryCode) {
    const mapped = COUNTRY_BUCKS_CURRENCY[countryCode];
    if (mapped && BUCKS_CURRENCIES.includes(mapped)) return mapped;
    return 'USD';
  }

  const SHORT_COUNTRY_NAMES = {
    US: 'US',
    GB: 'UK',
    IS: 'Iceland',
    IT: 'Italy',
    FR: 'France',
  };

  const SCROLL_LOCK_CLASS = 'geo-locale-popup--lock-scroll';
  const OPEN_CLASS = 'is-open';
  const OPEN_DROPDOWN_CLASS = 'is-open';

  /* ==============================
     Geo Detection
     ============================== */
  const GeoDetection = {
    async detect(debug) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url =
          root +
          'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true';
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (response.ok) {
          const data = await response.json();
          const detected = data.detected_values?.country;
          if (detected?.handle) {
            const result = {
              countryCode: detected.handle.toUpperCase(),
              countryName: detected.name || detected.handle,
            };
            if (debug) console.log('[geo-locale-popup] Detected:', result);
            return result;
          }
        }
      } catch (error) {
        if (debug) console.warn('[geo-locale-popup] Detection failed:', error);
      }
      if (debug) console.log('[geo-locale-popup] No country detected');
      return null;
    },
  };

  /* ==============================
     Market Mapping
     ============================== */
  const MarketMapping = {
    getLocaleForCountry(countryCode) {
      return COUNTRY_LOCALE_MAP[countryCode] || DEFAULT_LOCALE;
    },

    resolveLocaleForCountry(countryCode, availableLocales) {
      const preferred = this.getLocaleForCountry(countryCode);
      const locales = availableLocales || [];

      if (locales.includes(preferred)) return preferred;

      const partial = locales.find(
        (code) => code === preferred || code.startsWith(`${preferred}-`),
      );
      if (partial) return partial;

      if (locales.includes(DEFAULT_LOCALE)) return DEFAULT_LOCALE;

      const english = locales.find((code) => code.startsWith('en'));
      if (english) return english;

      return locales[0] || DEFAULT_LOCALE;
    },

    marketSelectionDiffers(current, countryCode, locale) {
      if (!current) return true;
      return current.country !== countryCode || current.locale !== locale;
    },

    resolveMarketCountry(locale, availableMarkets, preferredCountry, defaultCountry) {
      const markets = availableMarkets || [];

      if (preferredCountry) {
        const preferred = markets.find((m) => m.iso === preferredCountry);
        if (preferred) return preferred.iso;
        // Keep detected country for popup even if missing from Markets list
        return preferredCountry;
      }
      const localeIso = { is: 'IS', it: 'IT', fr: 'FR' }[locale];
      if (localeIso) {
        const match = markets.find((m) => m.iso === localeIso);
        if (match) return match.iso;
      }
      const fallback = markets.find((m) => m.iso === (defaultCountry || DEFAULT_COUNTRY));
      if (fallback) return fallback.iso;
      return markets[0]?.iso || DEFAULT_COUNTRY;
    },

    buildSuggestion(detected, availableMarkets, availableLocales, defaultCountry, debug) {
      const locale = this.resolveLocaleForCountry(
        detected.countryCode,
        availableLocales,
      );
      const countryCode = this.resolveMarketCountry(
        locale,
        availableMarkets,
        detected.countryCode,
        defaultCountry,
      );
      const market = availableMarkets.find((m) => m.iso === countryCode);
      const suggestion = {
        countryCode,
        countryName: market?.name || detected.countryName,
        locale,
        currency: market?.currency || resolveBucksCurrency(countryCode),
        flagUrl: market?.flagUrl || '',
        detectedCountryCode: detected.countryCode,
      };
      if (debug) console.log('[geo-locale-popup] Suggestion:', suggestion);
      return suggestion;
    },

    marketsMatch(current, suggested, detectedCountryCode) {
      if (!current || !suggested) return false;

      const countryMatches =
        current.country === suggested.countryCode ||
        (detectedCountryCode && current.country === detectedCountryCode);

      const localeMatches = current.locale === suggested.locale;

      return countryMatches && localeMatches;
    },

    shouldSkipPopup(current, suggested, detected, defaultCountry, actualCurrency) {
      if (!current || !suggested || !detected) return false;

      const currencyMatches =
        (actualCurrency || resolveBucksCurrency(current.country)) === suggested.currency;

      const onSuggestedMarket = this.marketsMatch(
        current,
        suggested,
        detected.countryCode,
      );

      if (!onSuggestedMarket || !currencyMatches) return false;

      // Only auto-skip for visitors already on the primary storefront (e.g. US/en/USD)
      const onPrimaryStorefront =
        current.country === defaultCountry && current.locale === DEFAULT_LOCALE;

      return onPrimaryStorefront && detected.countryCode === defaultCountry;
    },
  };

  /* ==============================
     Translations
     ============================== */
  const Translations = {
    parse(raw) {
      try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw || {};
      } catch {
        return {};
      }
    },

    getStrings(allTranslations, locale) {
      return allTranslations[locale] || allTranslations[DEFAULT_LOCALE] || {};
    },

    interpolate(template, vars) {
      if (!template) return '';
      return Object.entries(vars).reduce(
        (text, [key, value]) => text.replaceAll(`[${key}]`, value ?? ''),
        template,
      );
    },

    getCurrencyName(strings, currencyCode) {
      const key = `currency_${(currencyCode || '').toLowerCase()}`;
      return strings[key] || currencyCode || '';
    },
  };

  /* ==============================
     Storage (sessionStorage only)
     ============================== */
  const Storage = {
    PREF_KEY: 'geo_market_preference',
    CHOICE_KEY: 'geo_market_choice',
    LEGACY_CLEARED_KEY: 'geo_legacy_cleared',

    hasPreference() {
      try {
        return sessionStorage.getItem(this.PREF_KEY) === 'true';
      } catch {
        return false;
      }
    },

    save(choice) {
      try {
        sessionStorage.setItem(this.PREF_KEY, 'true');
        if (choice) sessionStorage.setItem(this.CHOICE_KEY, JSON.stringify(choice));
      } catch {
        /* unavailable */
      }
    },

    getChoice() {
      try {
        const raw = sessionStorage.getItem(this.CHOICE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },

    clearLegacyPersistence() {
      try {
        if (sessionStorage.getItem(this.LEGACY_CLEARED_KEY) === '1') return;
        sessionStorage.setItem(this.LEGACY_CLEARED_KEY, '1');
      } catch {
        return;
      }
      try {
        localStorage.removeItem(this.PREF_KEY);
        localStorage.removeItem(this.CHOICE_KEY);
        localStorage.removeItem('user_currency_preference');
        localStorage.removeItem('user_currency');
      } catch {
        /* unavailable */
      }
      const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';
      for (const name of [
        this.PREF_KEY,
        this.CHOICE_KEY,
        'user_currency_preference',
        'user_currency',
      ]) {
        document.cookie = `${name}=;expires=${expired};path=/;SameSite=Lax`;
      }
    },
  };

  /* ==============================
     BUCKS Currency Switch
     ============================== */
  const BucksFallback = {
    MAX_RETRIES: 20,
    RETRY_INTERVAL: 600,

    async changeCurrency(currencyCode, debug) {
      if (!currencyCode || !BUCKS_CURRENCIES.includes(currencyCode)) return false;

      if (this._clickBucksOption(currencyCode) || this._changeBucksSelect(currencyCode)) {
        if (debug) console.log('[geo-locale-popup] BUCKS switched (fast path):', currencyCode);
        return true;
      }

      let switcherOpened = false;
      for (let attempt = 0; attempt < this.MAX_RETRIES; attempt++) {
        if (!switcherOpened) switcherOpened = this._openSwitcher();
        await this._wait(this.RETRY_INTERVAL);
        if (this._clickBucksOption(currencyCode) || this._changeBucksSelect(currencyCode)) {
          if (debug) {
            console.log('[geo-locale-popup] BUCKS switched after', attempt + 1, 'tries:', currencyCode);
          }
          return true;
        }
      }
      if (debug) console.warn('[geo-locale-popup] BUCKS switch failed:', currencyCode);
      return false;
    },

    _wait(ms) {
      return new Promise((r) => setTimeout(r, ms));
    },

    _openSwitcher() {
      const trigger =
        document.querySelector('.hxoSelectTheme .buckscc-select button') ||
        document.querySelector('.hxoSelectTheme .buckscc-select') ||
        document.querySelector('.buckscc-select button') ||
        document.querySelector('.buckscc-select');
      if (!trigger) return false;
      try {
        trigger.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        trigger.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
        trigger.click();
      } catch {
        return false;
      }
      return true;
    },

    _matchesCurrencyOption(currencyCode, text, dataVal) {
      if (dataVal === currencyCode) return true;
      if (text.includes(currencyCode)) return true;
      const aliases = CURRENCY_TEXT_ALIASES[currencyCode];
      return aliases ? aliases.some((alias) => text.includes(alias)) : false;
    },

    _clickBucksOption(currencyCode) {
      const optionLists = document.querySelectorAll('.buckscc-select-options');
      for (const list of optionLists) {
        const items = list.querySelectorAll('li');
        for (const item of items) {
          const text = (item.textContent || '').trim().toUpperCase();
          const dataVal = (
            item.getAttribute('data-currency') ||
            item.getAttribute('data-value') ||
            item.getAttribute('data-code') ||
            ''
          ).toUpperCase();
          if (this._matchesCurrencyOption(currencyCode, text, dataVal)) {
            item.click();
            return true;
          }
        }
      }
      return false;
    },

    _changeBucksSelect(currencyCode) {
      const selects = document.querySelectorAll(
        '.hxoSelectTheme select, select[name="currency"]',
      );
      for (const select of selects) {
        const option = select.querySelector(`option[value="${currencyCode}"]`);
        if (option) {
          select.value = currencyCode;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
      return false;
    },
  };

  /* ==============================
     Helpers
     ============================== */
  function isDebugEnabled() {
    try {
      if (localStorage.getItem('geo_debug') === '1') return true;
    } catch {
      /* unavailable */
    }
    return new URLSearchParams(window.location.search).has('geo_debug');
  }

  function parseJsonAttr(element, attr, fallback) {
    try {
      const raw = element.getAttribute(attr);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  }

  function getShortCountryName(market) {
    if (!market) return '';
    return SHORT_COUNTRY_NAMES[market.iso] || market.name || market.iso || '';
  }

  /* ==============================
     Web Component
     ============================== */
  class GeoLocalePopup extends HTMLElement {
    constructor() {
      super();
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onContinue = this.onContinue.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onUpdate = this.onUpdate.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onDropdownToggle = this.onDropdownToggle.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      if (window.Shopify?.designMode) return;

      this.debug = isDebugEnabled();
      this.currentMarket = parseJsonAttr(this, 'data-current-market', {});
      const rawMarkets = parseJsonAttr(this, 'data-available-markets', []);
      this.availableMarkets = rawMarkets.map((m) => {
        const currency = resolveBucksCurrency(m.iso);
        return {
          ...m,
          currency,
          currencySymbol: CURRENCY_SYMBOLS[currency] || currency,
        };
      });
      this.actualCurrency = this.currentMarket?.currency || 'USD';
      if (this.currentMarket?.country) {
        const bucksCurrency = resolveBucksCurrency(this.currentMarket.country);
        this.currentMarket.bucksCurrency = bucksCurrency;
        this.currentMarket.currencySymbol =
          CURRENCY_SYMBOLS[bucksCurrency] || bucksCurrency;
      }
      this.defaultCountry = this.getAttribute('data-default-country') || DEFAULT_COUNTRY;
      this.availableLocales = parseJsonAttr(this, 'data-available-locales', []);

      const translationsEl = document.querySelector('[data-geo-translations]');
      this.translations = Translations.parse(translationsEl?.textContent);

      this.overlay = this.querySelector('[data-geo-overlay]');
      this.closeBtn = this.querySelector('[data-geo-close]');
      this.continueBtn = this.querySelector('[data-geo-continue]');
      this.changeBtn = this.querySelector('[data-geo-change]');
      this.updateBtn = this.querySelector('[data-geo-update]');
      this.cancelBtn = this.querySelector('[data-geo-cancel]');
      this.titleEl = this.querySelector('[data-geo-title]');
      this.currencyLineEl = this.querySelector('[data-geo-currency-line]');
      this.shippingLineEl = this.querySelector('[data-geo-shipping-line]');
      this.pickerTitleEl = this.querySelector('[data-geo-picker-title]');
      this.introView = this.querySelector('[data-geo-view="intro"]');
      this.pickerView = this.querySelector('[data-geo-view="picker"]');
      this.dropdownEl = this.querySelector('[data-geo-dropdown]');
      this.dropdownToggle = this.querySelector('[data-geo-dropdown-toggle]');
      this.dropdownValue = this.querySelector('[data-geo-dropdown-value]');
      this.dropdownList = this.querySelector('[data-geo-dropdown-list]');

      this.form = this.querySelector('#GeoLocaleForm');
      this.countryInput = this.querySelector('[data-geo-country-input]');
      this.localeInput = this.querySelector('[data-geo-locale-input]');
      this.returnToInput = this.querySelector('[data-geo-return-to]');

      this._updateReturnTo();
      Storage.clearLegacyPersistence();

      if (Storage.hasPreference()) {
        this._runBucksFallbackIfNeeded();
        return;
      }

      this.setEventListeners();
      this.detectAndShow();
    }

    disconnectedCallback() {
      this.removeEventListeners();
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    }

    setEventListeners() {
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('click', this.onDocumentClick);
      this.overlay?.addEventListener('click', this.onOverlayClick);
      this.closeBtn?.addEventListener('click', this.onClose);
      this.continueBtn?.addEventListener('click', this.onContinue);
      this.changeBtn?.addEventListener('click', this.onChange);
      this.updateBtn?.addEventListener('click', this.onUpdate);
      this.cancelBtn?.addEventListener('click', this.onCancel);
      this.dropdownToggle?.addEventListener('click', this.onDropdownToggle);
    }

    removeEventListeners() {
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('click', this.onDocumentClick);
      this.overlay?.removeEventListener('click', this.onOverlayClick);
      this.closeBtn?.removeEventListener('click', this.onClose);
      this.continueBtn?.removeEventListener('click', this.onContinue);
      this.changeBtn?.removeEventListener('click', this.onChange);
      this.updateBtn?.removeEventListener('click', this.onUpdate);
      this.cancelBtn?.removeEventListener('click', this.onCancel);
      this.dropdownToggle?.removeEventListener('click', this.onDropdownToggle);
    }

    _stripLocaleFromPath(path) {
      if (!path || path === '/') return '/';

      for (const locale of this.availableLocales || []) {
        const prefix = `/${locale}`;
        if (path === prefix) return '/';
        if (path.startsWith(`${prefix}/`)) {
          return path.slice(prefix.length) || '/';
        }
      }

      return path;
    }

    _updateReturnTo() {
      if (!this.returnToInput) return;
      const path = this._stripLocaleFromPath(window.location.pathname || '/');
      const search = window.location.search || '';
      this.returnToInput.value = path + search;
    }

    async detectAndShow() {
      const geo = await GeoDetection.detect(this.debug);

      let suggestion = null;
      if (geo) {
        suggestion = MarketMapping.buildSuggestion(
          geo,
          this.availableMarkets,
          this.availableLocales,
          this.defaultCountry,
          this.debug,
        );

        const marketExists = this.availableMarkets.some(
          (m) => m.iso === suggestion.countryCode,
        );
        if (!marketExists && this.debug) {
          console.warn(
            '[geo-locale-popup] Country not in Markets list:',
            suggestion.countryCode,
            '— showing popup anyway',
          );
        }

        if (
          suggestion &&
          MarketMapping.shouldSkipPopup(
            this.currentMarket,
            suggestion,
            geo,
            this.defaultCountry,
            this.actualCurrency,
          )
        ) {
          Storage.save({ action: 'silent_match', ...suggestion });
          if (this.debug) {
            console.log('[geo-locale-popup] Primary storefront match, skipping popup', {
              detected: geo,
              current: this.currentMarket,
              suggestion,
            });
          }
          return;
        }

        if (this.debug) {
          console.log('[geo-locale-popup] Showing popup', {
            detected: geo,
            current: this.currentMarket,
            suggestion,
            marketExists,
          });
        }
      }

      this._suggestion = suggestion;
      this._selectedCountryCode =
        suggestion?.countryCode || this.currentMarket?.country || this.defaultCountry;

      this.populateUI();
      this.renderDropdown();
      this.updateDropdownValue();
      this.showIntroView();

      setTimeout(() => this.open(), 1500);
    }

    _getIntroMarket() {
      if (!this._suggestion) return null;
      const market = this.availableMarkets.find(
        (m) => m.iso === this._suggestion.countryCode,
      );
      return {
        countryCode: this._suggestion.countryCode,
        countryName: market?.name || this._suggestion.countryName,
        locale: this._suggestion.locale,
        currency: market?.currency || this._suggestion.currency,
      };
    }

    populateUI() {
      const introMarket = this._getIntroMarket();
      const uiLocale = introMarket?.locale || this.currentMarket?.locale || DEFAULT_LOCALE;
      this._uiLocale = uiLocale;
      const strings = Translations.getStrings(this.translations, uiLocale);

      const countryName = introMarket?.countryName || '';
      const currency = introMarket?.currency || 'USD';
      const currencyName = Translations.getCurrencyName(strings, currency);

      if (this.titleEl && introMarket) {
        this.titleEl.textContent = Translations.interpolate(strings.location_set_to, {
          country: countryName,
        });
      }
      if (this.currencyLineEl && introMarket) {
        this.currencyLineEl.textContent = Translations.interpolate(strings.shop_currency, {
          currency_name: currencyName,
          currency,
        });
      }
      if (this.shippingLineEl && introMarket) {
        this.shippingLineEl.textContent = Translations.interpolate(strings.shipping_methods, {
          country: countryName,
        });
      }
      if (this.pickerTitleEl) {
        this.pickerTitleEl.textContent = strings.picker_title || 'Change country';
      }
      if (this.continueBtn) this.continueBtn.textContent = strings.continue || 'Continue';
      if (this.changeBtn) this.changeBtn.textContent = strings.change_country || 'Change country';
      if (this.updateBtn) this.updateBtn.textContent = strings.update || 'Update';
      if (this.cancelBtn) this.cancelBtn.textContent = strings.cancel || 'Cancel';
      if (this.closeBtn) this.closeBtn.setAttribute('aria-label', strings.aria_close || 'Close');
      if (this.dropdownToggle) {
        this.dropdownToggle.setAttribute('aria-label', strings.aria_dropdown || 'Select country');
      }
      if (this.overlay) {
        this.overlay.setAttribute('aria-label', strings.aria_label || 'Region selection');
      }
    }

    /* ---------- Dropdown ---------- */
    renderDropdown() {
      if (!this.dropdownList) return;
      this.dropdownList.innerHTML = '';

      const markets = [...this.availableMarkets].sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      for (const market of markets) {
        const li = document.createElement('li');
        li.className = 'geo-locale-popup__dropdown-option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '0');
        li.setAttribute('data-country-code', market.iso);
        if (market.iso === this._selectedCountryCode) {
          li.setAttribute('aria-selected', 'true');
          li.classList.add('is-selected');
        }
        li.appendChild(this._buildMarketLabel(market));
        li.addEventListener('click', () => this.selectCountry(market.iso));
        li.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectCountry(market.iso);
          }
        });
        this.dropdownList.appendChild(li);
      }
    }

    _buildMarketLabel(market) {
      const wrap = document.createElement('span');
      wrap.className = 'geo-locale-popup__market';

      if (market.flagUrl) {
        const img = document.createElement('img');
        img.src = market.flagUrl;
        img.alt = '';
        img.width = 24;
        img.height = 18;
        img.className = 'geo-locale-popup__flag';
        wrap.appendChild(img);
      }

      const name = document.createElement('span');
      name.className = 'geo-locale-popup__market-name';
      name.textContent = market.name;
      wrap.appendChild(name);

      const currency = document.createElement('span');
      currency.className = 'geo-locale-popup__market-currency';
      currency.textContent =
        market.currency +
        (market.currencySymbol && market.currencySymbol !== market.currency
          ? ' ' + market.currencySymbol
          : '');
      wrap.appendChild(currency);

      return wrap;
    }

    updateDropdownValue() {
      if (!this.dropdownValue) return;
      const market = this.availableMarkets.find((m) => m.iso === this._selectedCountryCode);
      if (!market) return;
      this.dropdownValue.innerHTML = '';
      this.dropdownValue.appendChild(this._buildMarketLabel(market));
    }

    selectCountry(countryCode) {
      this._selectedCountryCode = countryCode;
      this.dropdownList?.querySelectorAll('.geo-locale-popup__dropdown-option').forEach((li) => {
        const active = li.getAttribute('data-country-code') === countryCode;
        li.classList.toggle('is-selected', active);
        if (active) {
          li.setAttribute('aria-selected', 'true');
        } else {
          li.removeAttribute('aria-selected');
        }
      });
      this.updateDropdownValue();
      this.closeDropdown();
    }

    openDropdown() {
      if (!this.dropdownEl || !this.dropdownList) return;
      this.dropdownEl.classList.add(OPEN_DROPDOWN_CLASS);
      this.dropdownList.removeAttribute('hidden');
      this.dropdownToggle?.setAttribute('aria-expanded', 'true');
      const selected = this.dropdownList.querySelector('.is-selected');
      selected?.scrollIntoView({ block: 'nearest' });
    }

    closeDropdown() {
      if (!this.dropdownEl || !this.dropdownList) return;
      this.dropdownEl.classList.remove(OPEN_DROPDOWN_CLASS);
      this.dropdownList.setAttribute('hidden', '');
      this.dropdownToggle?.setAttribute('aria-expanded', 'false');
    }

    onDropdownToggle(event) {
      event.stopPropagation();
      if (this.dropdownEl?.classList.contains(OPEN_DROPDOWN_CLASS)) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    }

    onDocumentClick(event) {
      if (!this.dropdownEl) return;
      if (!this.dropdownEl.contains(event.target)) this.closeDropdown();
    }

    /* ---------- View transitions ---------- */
    showIntroView() {
      this.introView?.removeAttribute('hidden');
      this.pickerView?.setAttribute('hidden', '');
      this.closeDropdown();
    }

    showPickerView() {
      this.introView?.setAttribute('hidden', '');
      this.pickerView?.removeAttribute('hidden');
    }

    /* ---------- Open/close ---------- */
    open() {
      if (!this.overlay) return;
      this.overlay.classList.add(OPEN_CLASS);
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
      setTimeout(() => this.continueBtn?.focus(), 300);
    }

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove(OPEN_CLASS);
      this.closeDropdown();
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    }

    /* ---------- Handlers ---------- */
    onClose() {
      this.dismiss();
    }

    onCancel() {
      this.showIntroView();
    }

    onChange() {
      this.showPickerView();
    }

    async onContinue() {
      const introMarket = this._getIntroMarket();

      if (introMarket) {
        Storage.save({ action: 'continued', ...introMarket });

        const marketChanged = MarketMapping.marketSelectionDiffers(
          this.currentMarket,
          introMarket.countryCode,
          introMarket.locale,
        );

        if (marketChanged) {
          if (this.debug) {
            console.log('[geo-locale-popup] Continue — applying suggested market:', introMarket);
          }
          this.submitLocalization(introMarket.countryCode, introMarket.locale);
          return;
        }

        await this._finishWithCurrencySwitch(introMarket.currency);
        return;
      }

      Storage.save({
        action: 'continued',
        countryCode: this.currentMarket?.country,
        locale: this.currentMarket?.locale,
        currency: this.actualCurrency,
      });
      this.close();
    }

    async onUpdate() {
      const countryCode = this._selectedCountryCode;
      if (!countryCode) return;
      const market = this.availableMarkets.find((m) => m.iso === countryCode);
      const locale = MarketMapping.resolveLocaleForCountry(
        countryCode,
        this.availableLocales,
      );
      const currency = market?.currency;

      Storage.save({
        action: 'updated',
        countryCode,
        countryName: market?.name || countryCode,
        locale,
        currency,
      });

      const marketChanged = MarketMapping.marketSelectionDiffers(
        this.currentMarket,
        countryCode,
        locale,
      );

      if (marketChanged) {
        if (this.debug) {
          console.log('[geo-locale-popup] Switching market + language:', {
            from: this.currentMarket,
            to: { countryCode, locale, currency },
          });
        }
        this.submitLocalization(countryCode, locale);
        return;
      }

      await this._finishWithCurrencySwitch(currency);
    }

    _needsCurrencySwitch(targetCurrency) {
      if (!targetCurrency || !BUCKS_CURRENCIES.includes(targetCurrency)) return false;
      return this.actualCurrency !== targetCurrency;
    }

    async _finishWithCurrencySwitch(currency) {
      this.close();
      if (!this._needsCurrencySwitch(currency)) return;
      if (this.debug) {
        console.log('[geo-locale-popup] Switching BUCKS currency:', {
          from: this.actualCurrency,
          to: currency,
        });
      }
      await BucksFallback.changeCurrency(currency, this.debug);
    }

    dismiss() {
      Storage.save({
        action: 'dismissed',
        countryCode: this.currentMarket?.country,
        locale: this.currentMarket?.locale,
        currency: this.actualCurrency,
      });
      this.close();
    }

    submitLocalization(countryCode, localeCode) {
      if (!this.form || !this.countryInput || !this.localeInput) return;
      this._updateReturnTo();
      this.countryInput.value = countryCode;
      this.localeInput.value = localeCode;
      if (this.debug) {
        console.log('[geo-locale-popup] Submitting localization:', {
          countryCode,
          localeCode,
          returnTo: this.returnToInput?.value,
        });
      }
      this.form.submit();
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        if (this.dropdownEl?.classList.contains(OPEN_DROPDOWN_CLASS)) {
          this.closeDropdown();
          return;
        }
        if (!this.pickerView?.hasAttribute('hidden')) {
          this.showIntroView();
          return;
        }
        this.dismiss();
      }
    }

    onOverlayClick(event) {
      if (event.target === this.overlay) this.dismiss();
    }

    async _runBucksFallbackIfNeeded() {
      const choice = Storage.getChoice();
      const expected = choice?.currency;
      if (!expected || !BUCKS_CURRENCIES.includes(expected)) return;
      await BucksFallback.changeCurrency(expected, this.debug);
    }
  }

  customElements.define('geo-locale-popup', GeoLocalePopup);
})();
